
import React, { useState, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UploadCloud, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { importLeadsFromCSV, getCSVTemplate, downloadCSV } from "@/services/csvService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface CSVImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: (results: { success: number; failed: number }) => void;
}

const CSVImportDialog: React.FC<CSVImportDialogProps> = ({
  open,
  onOpenChange,
  onImportComplete
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStage, setImportStage] = useState<"idle" | "processing" | "complete">("idle");
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0);
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [importResult, setImportResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      // Reset import state when a new file is selected
      setImportResult(null);
      setImportStage("idle");
    }
  };

  const handleDownloadTemplate = () => {
    const template = getCSVTemplate();
    downloadCSV(template, "leads-import-template.csv");
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file to import.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    setImportProgress(0);
    setImportResult(null);
    setImportStage("processing");

    try {
      // Read file content
      const fileContent = await file.text();
      
      // Import leads with progress tracking
      const results = await importLeadsFromCSV(fileContent, (current, total) => {
        setCurrentLeadIndex(current + 1);
        setTotalLeadsCount(total);
        const progress = Math.round((current / total) * 100);
        setImportProgress(progress);
      });
      
      setImportResult(results);
      setImportStage("complete");
      
      if (results.success > 0) {
        toast({
          title: "Import Completed",
          description: `Successfully imported ${results.success} lead${results.success !== 1 ? 's' : ''}.${
            results.failed > 0 ? ` ${results.failed} lead${results.failed !== 1 ? 's' : ''} failed.` : ''
          }`,
        });
      } else {
        toast({
          title: "Import Failed",
          description: "No leads were imported. Please check the file format and try again.",
          variant: "destructive",
        });
      }
      
      if (onImportComplete) {
        onImportComplete(results);
      }
    } catch (error) {
      console.error('Error importing leads:', error);
      toast({
        title: "Import Failed",
        description: "An error occurred while importing leads. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setImportResult(null);
        setImportStage("idle");
      } else {
        toast({
          title: "Invalid File Type",
          description: "Please upload a CSV file.",
          variant: "destructive",
        });
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setImportResult(null);
    setImportStage("idle");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getProgressLabel = () => {
    if (importStage === "processing" && totalLeadsCount > 0) {
      return `Processing lead ${currentLeadIndex} of ${totalLeadsCount}`;
    }
    return "Importing leads...";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Leads from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import leads. Each lead will be matched against Google Places for enhanced data.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Template download button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={isImporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
          
          {/* File upload area */}
          <div
            className={`border-2 border-dashed rounded-md p-6 text-center ${
              file ? 'border-primary' : 'border-muted-foreground/20'
            }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".csv"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={isImporting}
            />
            
            {file ? (
              <div className="space-y-2">
                <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <Button variant="outline" size="sm" onClick={handleReset} disabled={isImporting}>
                  Choose Different File
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <UploadCloud className="h-10 w-10 text-muted-foreground/50 mx-auto" />
                <p>Drag and drop your CSV file here, or</p>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isImporting}>
                  Browse Files
                </Button>
              </div>
            )}
          </div>
          
          {/* Progress indicator */}
          {isImporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{getProgressLabel()}</span>
                <span>{importProgress}%</span>
              </div>
              <Progress value={importProgress} className="h-2" />
            </div>
          )}
          
          {/* Import results */}
          {importResult && (
            <div className="space-y-2">
              <Alert variant={importResult.failed > 0 ? "destructive" : "default"}>
                <div className="flex items-start">
                  {importResult.failed > 0 ? (
                    <AlertCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 mr-2" />
                  )}
                  <div>
                    <AlertTitle>Import Results</AlertTitle>
                    <AlertDescription>
                      {importResult.success > 0 && (
                        <p>Successfully imported {importResult.success} lead{importResult.success !== 1 ? 's' : ''}.</p>
                      )}
                      {importResult.failed > 0 && (
                        <p>Failed to import {importResult.failed} lead{importResult.failed !== 1 ? 's' : ''}.</p>
                      )}
                      
                      {importResult.errors.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm font-medium">Show Error Details</summary>
                          <ul className="mt-2 text-sm list-disc list-inside">
                            {importResult.errors.slice(0, 5).map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                            {importResult.errors.length > 5 && (
                              <li>...and {importResult.errors.length - 5} more errors</li>
                            )}
                          </ul>
                        </details>
                      )}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImport}
            disabled={!file || isImporting}
          >
            {isImporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <UploadCloud className="h-4 w-4 mr-2" />
                Import Leads
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportDialog;
